import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Checkbox, InputLabel} from '@/web-components';
import {FlexColumn, FlexRow, DeprecatedText as Text} from '@forecast-it/design-system';
import Dropdown from '../../../forecast-app/shared/components/dropdowns/dropdown';

type Period = {
	id: string;
	name: string;
	startYear: number;
	startMonth: number;
	startDay: number;
	endYear: number;
	endMonth: number;
	endDay: number;
};

type Props = {
	partOfFixedPrice: boolean;
	unlockedPeriods: Period[];
	selectedPeriodId: string | null;
	defaultPeriodId: string | null;
	onChange: (periodId: string | null) => void;
	locked: boolean;
};

const SectionWrapper = styled.div`
	margin-top: 14px;
`;

const RetainerPeriodSection: React.FC<Props> = ({
	partOfFixedPrice,
	unlockedPeriods,
	selectedPeriodId,
	defaultPeriodId,
	onChange,
	locked,
}) => {
	const {formatMessage} = useIntl();

	const hasUnlockedPeriods = unlockedPeriods.length > 0;
	const isEnabled = partOfFixedPrice && hasUnlockedPeriods && !locked;
	const isChecked = !!selectedPeriodId;
	const showNoPeriodsMessage = partOfFixedPrice && !hasUnlockedPeriods && !locked;
	const showDropdown = isChecked && hasUnlockedPeriods;

	const periodOptions = unlockedPeriods.map(period => ({value: period.id, label: period.name}));

	const handleCheckboxChange = () => {
		if (!isChecked) {
			onChange(defaultPeriodId ?? unlockedPeriods[0].id);
		} else {
			onChange(null);
		}
	};

	const handleDropdownChange = (selected: {value: string; label: string} | null) => {
		onChange(selected ? selected.value : null);
	};

	return (
		<SectionWrapper>
			<FlexColumn gap={'s'}>
				<FlexRow gap={'s'}>
					<Checkbox
						checked={isChecked}
						disabled={!isEnabled}
						onChange={handleCheckboxChange}
						cy={'retainer-period-checkbox'}
					/>
					<Text>{formatMessage({id: 'expense_item_modal.include_on_a_period'})}</Text>
				</FlexRow>
				{showNoPeriodsMessage && (
					<Text type={'small'} color={'disabled'}>
						{formatMessage({id: 'expense_item_modal.no_unlocked_periods_available'})}
					</Text>
				)}
				{showDropdown && (
					<div>
						<InputLabel
							text={formatMessage({id: 'common.period'})}
							child={
								<Dropdown
									value={selectedPeriodId}
									options={periodOptions}
									onChange={handleDropdownChange}
									disabled={locked}
									multi={false}
									clearable={false}
									hideLabel
									dataCy={'retainer-period-dropdown'}
									buttonCy={'retainer-period-dropdown-arrow'}
								/>
							}
						/>
					</div>
				)}
			</FlexColumn>
		</SectionWrapper>
	);
};

export default RetainerPeriodSection;
